// @flow
import React, { Component } from 'react';
import i18n from 'app/common/lib/i18n';
import { pinterestLogoSvgDataURL } from 'app/common/react/components/pincode/PinterestLogoSvg';
import withResource, { type Resource } from 'app/common/lib/react/withResource';
import { Box, Spinner } from 'gestalt';

const LOGO_INLINE_URL = pinterestLogoSvgDataURL();
const COLLAGE_SIZE = 600;
const COLLAGE_OFFSET = 100;

type PincodeMainImageProps = {|
  className: ?string,
  id: string,
  imageUrls: Array<string>,
  onFetchingChange: (boolean) => void,
  pincodeImageResource: Resource<string>,
  size: number,
|};

function getCollageItemSvgStr(
  url: string,
  x: 'left' | 'right' | 'row',
  y: 'top' | 'bottom' | 'column',
) {
  return `
    <image id="image"
      x="${COLLAGE_OFFSET + (x === 'right' ? COLLAGE_SIZE / 2 : 0)}"
      y="${COLLAGE_OFFSET + (y === 'bottom' ? COLLAGE_SIZE / 2 : 0)}"
      width="${COLLAGE_SIZE / (x === 'row' ? 1 : 2)}"
      height="${COLLAGE_SIZE / (y === 'column' ? 1 : 2)}"
      xmlns:xlink="http://www.w3.org/1999/xlink"
      xlink:href="${url}"
      preserveAspectRatio="xMidYMid slice"
    />`;
}

function getCollageSvgStr(urls: Array<string>): string | void {
  switch (urls.length) {
    case 0:
    case 1:
      return undefined;
    case 2:
      return [
        getCollageItemSvgStr(urls[0], 'left', 'column'),
        getCollageItemSvgStr(urls[1], 'right', 'column'),
      ].join('');
    case 3:
      return [
        getCollageItemSvgStr(urls[0], 'left', 'column'),
        getCollageItemSvgStr(urls[1], 'right', 'top'),
        getCollageItemSvgStr(urls[2], 'right', 'bottom'),
      ].join('');
    default:
      return [
        getCollageItemSvgStr(urls[0], 'left', 'top'),
        getCollageItemSvgStr(urls[1], 'right', 'top'),
        getCollageItemSvgStr(urls[2], 'left', 'bottom'),
        getCollageItemSvgStr(urls[3], 'right', 'bottom'),
      ].join('');
  }
}

function replaceImageWithCollageIfNecessary(svgString: string, urls: Array<string>) {
  const collageSvgStr = getCollageSvgStr(urls);
  return collageSvgStr ? svgString.replace(/<image[^>]*>/, collageSvgStr) : svgString;
}

export class PincodeMainImage extends Component<PincodeMainImageProps> {
  componentDidMount() {
    const { pincodeImageResource } = this.props;
    if (!pincodeImageResource.isFetching) {
      this.handleFetchChange();
    }
  }

  componentDidUpdate(prevProps: PincodeMainImageProps) {
    if (this.props.pincodeImageResource.isFetching !== prevProps.pincodeImageResource.isFetching) {
      this.handleFetchChange();
    }
  }

  handleFetchChange = () => {
    const { pincodeImageResource, onFetchingChange } = this.props;
    onFetchingChange(pincodeImageResource.isFetching);
  };

  render() {
    const {
      className,
      imageUrls,
      size,
      pincodeImageResource: { data: svgString, isLoaded },
    } = this.props;
    const width = size;
    const height = size;
    return (
      <Box display="flex" justifyContent="center" alignItems="center" width={width} height={height}>
        {isLoaded &&
          !!svgString && (
            <div
              className={className}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: replaceImageWithCollageIfNecessary(svgString, imageUrls),
              }}
              style={{ width, height }}
            />
          )}
        <Spinner
          accessibilityLabel={i18n._(
            'Loading Pincode Image',
            'accessibility label for loading the Pincode image',
          )}
          show={!svgString}
        />
      </Box>
    );
  }
}

export default withResource({
  name: 'PincodeImageResource',
  key: 'pincodeImageResource',
  options: ({ id, imageUrls }: PincodeMainImageProps) => ({
    object_id: id,
    hero_image_url: imageUrls[0] || LOGO_INLINE_URL,
  }),
})(PincodeMainImage);
